import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Tracks whether any form surface has unsaved state so the release auto-reload (SIM-1670) cannot
 * reload over edits. Fail-closed: `useIsReloadDirty()` returns `true` outside a provider, so a
 * watcher mounted without one blocks reload rather than risk unsaved work. State and dispatch are
 * separate contexts so a stable dispatch stops `useDeclareReloadSafe` looping.
 */

interface DirtyFormDispatch {
  setDirty: (id: string, dirty: boolean) => void;
  clear: (id: string) => void;
}

const DirtyFormStateContext = createContext<boolean | null>(null);
const DirtyFormDispatchContext = createContext<DirtyFormDispatch | null>(null);

export function DirtyFormProvider({ children }: { children: ReactNode }) {
  const [dirtyIds, setDirtyIds] = useState<ReadonlySet<string>>(() => new Set());

  const setDirty = useCallback((id: string, dirty: boolean) => {
    setDirtyIds((prev) => {
      if (dirty === prev.has(id)) return prev;
      const next = new Set(prev);
      if (dirty) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const clear = useCallback((id: string) => {
    setDirtyIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const dispatch = useMemo<DirtyFormDispatch>(() => ({ setDirty, clear }), [setDirty, clear]);

  return (
    <DirtyFormDispatchContext.Provider value={dispatch}>
      <DirtyFormStateContext.Provider value={dirtyIds.size > 0}>
        {children}
      </DirtyFormStateContext.Provider>
    </DirtyFormDispatchContext.Provider>
  );
}

/**
 * Whether any registered form is currently dirty. Outside a provider the
 * default is `true` (fail-closed — see file header).
 */
export function useIsReloadDirty(): boolean {
  const state = useContext(DirtyFormStateContext);
  return state ?? true;
}

/**
 * Declare whether the calling surface is currently SAFE to auto-reload — i.e.
 * has no unsaved changes and no in-flight save. Call it from any form-bearing
 * surface (e.g. `useDeclareReloadSafe(!isDirty && !isSaving)`); it registers on
 * mount and unregisters on unmount. A no-op outside a provider.
 */
export function useDeclareReloadSafe(safe: boolean): void {
  const dispatch = useContext(DirtyFormDispatchContext);
  const id = useId();
  useEffect(() => {
    if (!dispatch) return;
    dispatch.setDirty(id, !safe);
    return () => dispatch.clear(id);
  }, [dispatch, id, safe]);
}
